import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, Check, Loader2, PenLine, Clock } from "lucide-react";
import ModelIcon from "./ModelIcon";

/**
 * ResearchProgress - live "what the AI is doing right now" panel.
 *
 * Every value shown here is DERIVED from props the grid already receives
 * (`selectedModels`, `responses`, `isLoading`). It fetches nothing, stores
 * nothing, and changes no application state — the only local state is a
 * cosmetic elapsed-time counter.
 *
 * Per-model phase is inferred as:
 *   error                        -> failed
 *   loading + no text yet        -> connecting
 *   loading + text streaming in  -> writing
 *   not loading + text           -> complete
 *   otherwise                    -> queued
 */

const PHASE = {
  queued: { label: "Queued", tone: "queued", Icon: Clock },
  connecting: { label: "Connecting", tone: "active", Icon: Loader2 },
  writing: { label: "Writing analysis", tone: "active", Icon: PenLine },
  complete: { label: "Complete", tone: "complete", Icon: Check },
  failed: { label: "Failed", tone: "failed", Icon: AlertTriangle },
};

function getPhase(responseData, isGridLoading) {
  if (!responseData) return isGridLoading ? "queued" : "queued";
  if (responseData.error) return "failed";
  const hasText = !!(responseData.text && responseData.text.trim());
  if (responseData.loading) return hasText ? "writing" : "connecting";
  return hasText ? "complete" : "queued";
}

function formatElapsed(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return m > 0 ? `${m}m ${String(s).padStart(2, "0")}s` : `${s}s`;
}

export default function ResearchProgress({
  models = [],
  responses = {},
  isLoading = false,
  // Set while Comparative Analysis or Final Synthesis is running. The rows
  // below still describe the initial per-model analysis, so the panel names
  // the stage that is actually in flight and drops the "4/4" that QA read as
  // the comparison's own progress.
  stageLabel = null,
}) {
  const [elapsed, setElapsed] = useState(0);
  const startedAtRef = useRef(null);

  // Cosmetic timer — runs only while the query is in flight.
  useEffect(() => {
    if (!isLoading) {
      startedAtRef.current = null;
      return undefined;
    }
    if (startedAtRef.current === null) {
      startedAtRef.current = Date.now();
      setElapsed(0);
    }
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [isLoading]);

  const rows = useMemo(
    () =>
      models.map((model) => ({
        model,
        phase: getPhase(responses[model], isLoading),
      })),
    [models, responses, isLoading],
  );

  const doneCount = rows.filter(
    (r) => r.phase === "complete" || r.phase === "failed",
  ).length;
  const pct = rows.length ? Math.round((doneCount / rows.length) * 100) : 0;
  const activeCount = rows.filter(
    (r) => r.phase === "writing" || r.phase === "connecting",
  ).length;

  if (!rows.length) return null;

  const anyStarted = rows.some((r) => r.phase !== "queued");
  const laterStage = isLoading && !!stageLabel;
  const headline = laterStage
    ? stageLabel
    : isLoading
    ? activeCount > 0
      ? `Analysing across ${activeCount} model${activeCount === 1 ? "" : "s"}`
      : "Preparing analysis"
    : anyStarted
      ? "Research complete"
      : "Ready to run";

  return (
    <motion.section
      className="rs-progress"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24, ease: [0.32, 0.72, 0, 1] }}
      aria-live="polite"
    >
      <header className="rs-progress-head">
        <div className="rs-progress-headline">
          {isLoading && (
            <span className="ds-dot ds-dot-live rs-progress-live" aria-hidden="true" />
          )}
          <h3>{headline}</h3>
        </div>

        <div className="rs-progress-meta">
          {anyStarted && !laterStage && (
            <span className="rs-progress-count">
              {doneCount}/{rows.length}
            </span>
          )}
          {isLoading && (
            <span className="rs-progress-elapsed">{formatElapsed(elapsed)}</span>
          )}
        </div>
      </header>

      <div className="ds-progress rs-progress-rail">
        <motion.div
          className={`ds-progress-bar${laterStage ? " rs-progress-bar-indeterminate" : ""}`}
          initial={false}
          animate={{ width: laterStage ? "100%" : `${pct}%` }}
          transition={{ duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
        />
      </div>

      <ul className="rs-progress-list">
        {rows.map(({ model, phase }) => {
          const { label, tone, Icon } = PHASE[phase];
          const spin = phase === "connecting";
          return (
            <li key={model} className={`rs-progress-item rs-phase-${tone}`}>
              <span className="rs-progress-brand" aria-hidden="true">
                <ModelIcon model={model} size={13} />
              </span>
              <span className="rs-progress-model">{model}</span>
              <span className="rs-progress-icon">
                <Icon size={12} className={spin ? "ds-spin" : undefined} />
              </span>
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={phase}
                  className="rs-progress-phase"
                  initial={{ opacity: 0, y: 3 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -3 }}
                  transition={{ duration: 0.16, ease: [0.32, 0.72, 0, 1] }}
                >
                  {label}
                </motion.span>
              </AnimatePresence>
            </li>
          );
        })}
      </ul>
    </motion.section>
  );
}
